/* eslint-disable max-lines */
import { useConversationIndexStore } from '@/core/conversations/conversation-index/conversation-index.store';
import { useMeStore } from '@/core/shared/me/me.store';

import { useConversationProProfileStore } from '../pro-profile/pro-profile.store';
import { ConversationIndexPersistence } from './conversation-index.persistence';
import ProProfileService from '@/core/conversations/pro-profile/pro-profile.service';
import JobService from '@/core/shared/job/job.service';
import { JobUtil } from '@/utils/job.util';

import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';
import {
  type AdvanceSelectedConversationResult,
  type ApplicationIndex,
  type ConversationIndex,
  type ConversationIndexListFetchResult,
  type SearchConversationIndex,
  SearchConversationOrderByFields,
} from '@/core/conversations/conversation-index/conversation-index.type';
import {
  isEligibleGlobalSearchQuery,
  isPhoneLikeQuery,
  normalizePhoneQueryDigits,
} from '@/core/conversations/conversation-index/global-search.util';
import { originsForDeepLinkedConversation } from '@/core/conversations/conversation-index/deep-linked-conversation.util';
import type { Job, UserProfileWithRelations } from '@factoryfixinc/ats-interfaces';
import { isEqual } from 'radash';
import { unique } from '@/utils/arrays/unique.util';
import TrackingService from '@/core/shared/tracking/tracking.service';
import { TrackingActionName } from '@/core/shared/tracking/tracking-actions';
import ProjectService from '@/core/shared/project/project.service';
import ConversationMessageService from '../message/conversation-message.service';
import type { UserMessageStatus } from '../message/types/get-user-message-status.type';
import type { DoNotContactSummary } from '../do-not-contact/do-not-contact.type';
import { ErrorService } from '@/core/shared/errors/error.service';

export default class ConversationIndexService {
  private store = useConversationIndexStore();
  private persistence = new ConversationIndexPersistence();

  private proProfileService = new ProProfileService();
  private projectService = new ProjectService();
  private jobService = new JobService();
  private conversationMessageService = new ConversationMessageService();

  private meStore = useMeStore();
  private conversationProfileStore = useConversationProProfileStore();

  public get selectedConversationIndex(): ConversationIndex | null {
    return this.store.selectedConversationIndex || null;
  }

  public set selectedConversationIndex(conversationIndex: ConversationIndex | null) {
    if (this.store.selectedConversationIndex?.proId !== conversationIndex?.proId) {
      this.conversationProfileStore.clearSelectedProMessageStatus();
    }
    this.store.selectedConversationIndex = conversationIndex;
  }

  // The candidate display name for the selected conversation. Centralized here so the
  // mobile header and composer do not each re-derive proName off the index.
  public get selectedProName(): string {
    return this.selectedConversationIndex?.proName ?? '';
  }

  public get conversationIndexSearch(): SearchConversationIndex {
    return this.store.conversationSearch;
  }

  public set conversationIndexSearch(ref: SearchConversationIndex) {
    this.store.conversationSearch = ref;
  }

  public get conversationIndexes(): ConversationIndex[] {
    return this.store.conversationIndexes || [];
  }

  public set conversationIndexes(conversationIndexes: ConversationIndex[]) {
    this.store.conversationIndexes = conversationIndexes;
  }

  public get loadingConversationIndexes(): boolean {
    return this.store.loadingConversationIndexes;
  }

  public set loadingConversationIndexes(loading: boolean) {
    this.store.loadingConversationIndexes = loading;
  }

  /** True from a job switch until its route run ends, unlike a list reload or poll. */
  public get isSwitchingJob(): boolean {
    return this.store.jobSwitchRequestId !== null;
  }

  public get loadingMoreConversations(): boolean {
    return this.store.loadingMoreConversations;
  }

  public set loadingMoreConversations(loading: boolean) {
    this.store.loadingMoreConversations = loading;
  }

  public get selectingConversationIndex(): boolean {
    return this.store.selectingConversationIndex;
  }

  public set selectingConversationIndex(selecting: boolean) {
    this.store.selectingConversationIndex = selecting;
  }

  public get selectedStatusList(): string[] {
    return this.store.selectedStatusList;
  }

  public set selectedStatusList(statuses: string[]) {
    this.store.selectedStatusList = statuses;
  }

  public get showSearchField(): boolean {
    return this.store.showSearchField;
  }

  public set showSearchField(show: boolean) {
    this.store.showSearchField = show;
  }

  public get visitedTabRouteName(): string {
    return this.store.visitedTabRouteName;
  }

  public set visitedTabRouteName(name: string) {
    this.store.visitedTabRouteName = name;
  }

  public get selectedStatusFilters(): string[] {
    return this.store.selectedStatusFilters;
  }

  public set selectedStatusFilters(statuses: string[]) {
    this.store.selectedStatusFilters = statuses;
  }

  public get latestListFetchResult(): ConversationIndexListFetchResult | null {
    return this.store.latestListFetchResult;
  }

  public get selectedProMessageStatus(): UserMessageStatus | undefined {
    if (!this.isSelectedProfileEmployer) return undefined;
    return this.conversationProfileStore.selectedProMessageStatus;
  }

  private get isSelectedProfileEmployer(): boolean {
    const employerId = this.conversationProfileStore.requestedEmployerId;
    return employerId === undefined || employerId === this.meStore.employer?.id;
  }

  public get selectedProDoNotContact(): DoNotContactSummary | null | undefined {
    const profile = this.conversationProfileStore.selectedPro;
    const employerId = this.conversationProfileStore.selectedProEmployerId;
    return (employerId === undefined || employerId === this.meStore.employer?.id) &&
      profile?.id === this.selectedConversationIndex?.proId
      ? profile?.doNotContact
      : null;
  }

  public get isMessagingDoNotContact(): boolean {
    return (
      !!this.selectedProDoNotContact?.active || this.selectedProMessageStatus?.doNotContact === true
    );
  }

  public get messageStatusRefreshState(): 'idle' | 'loading' | 'error' {
    return this.isSelectedProfileEmployer
      ? this.conversationProfileStore.messageStatusRefreshState
      : 'idle';
  }

  public get isMessagingDisabled(): boolean {
    return (
      this.isMessagingDoNotContact ||
      this.messageStatusRefreshState !== 'idle' ||
      this.selectedProMessageStatus?.canReceiveMessage === false
    );
  }

  public patchDoNotContact(employerId: number, proId: number, doNotContact: boolean): void {
    if (this.meStore.employer?.id !== employerId) return;
    for (const row of [
      ...(this.store.conversationIndexes ?? []),
      ...this.store.globalSearchResults,
      this.selectedConversationIndex,
    ]) {
      if (row?.proId === proId) row.doNotContact = doNotContact;
    }
  }

  public get lastPageWithResults(): number | null {
    return this.store.lastPageWithResults;
  }

  public get currentPage(): number {
    return this.conversationIndexSearch.pagination.page;
  }

  public get lastCandidateArchiveSearch(): string {
    return this.store.lastCandidateArchiveSearch;
  }

  public set lastCandidateArchiveSearch(search: string) {
    this.store.lastCandidateArchiveSearch = search;
  }

  private resetConversationIndexSearch(): void {
    this.store.conversationSearch = {
      onlyCurrentUser: false,
      onlyUnread: false,
      jobIds: [],
      conversationIds: [],
      applicationStatus: [
        JobApplicantStatus.CONTACT,
        JobApplicantStatus.NEW,
        JobApplicantStatus.CLIENT,
        JobApplicantStatus.REVIEW,
        JobApplicantStatus.INTERVIEW,
        JobApplicantStatus.OFFER,
        JobApplicantStatus.HIRED,
      ],
      origins: [],
      orderBy: {
        field: SearchConversationOrderByFields.LAST_MESSAGE_SENT_TS,
        direction: 'desc',
      },
      pagination: {
        page: 1,
        itemsPerPage: 25,
      },
    };
    // Reset lastPageWithResults when resetting search to ensure proper pagination
    this.store.lastPageWithResults = null;
  }

  public resetConversationIndexes(): void {
    this.conversationIndexes = [];
  }

  public resetConversationIndexesListAndSearch(): void {
    this.resetConversationIndexSearch();
    this.resetConversationIndexes();
  }

  public getConversationIndexJobIds(conversationIndexes?: ConversationIndex[]): number[] {
    const data = conversationIndexes || this.conversationIndexes;
    return data.map((r) => r?.applicationIndexes?.map((index) => index?.jobId) ?? [])?.flat() ?? [];
  }

  private getPrimaryApplicationJobIds(
    conversationIndexes: ConversationIndex[],
  ): [Set<number>, ConversationIndex[]] {
    const primaryApplicationJobIds = new Set<number>();
    const conversationIndexesWithPrimaryApplication = conversationIndexes.map(
      (conversationIndex: ConversationIndex) => {
        const primaryApplicationIndex = ProProfileService.findPrimaryApplication<ApplicationIndex>(
          conversationIndex.applicationIndexes,
        );
        if (primaryApplicationIndex?.jobId) {
          primaryApplicationJobIds.add(primaryApplicationIndex.jobId);
        }
        return { ...conversationIndex, primaryApplicationIndex: primaryApplicationIndex || {} };
      },
    );
    return [primaryApplicationJobIds, conversationIndexesWithPrimaryApplication];
  }

  /**
   * Updates one application's status in the list and selected conversation.
   * The row stays in place even when the new status is outside the active filter.
   */
  public patchConversationApplicationStatus(
    conversationId: ConversationIndex['conversationId'],
    applicationId: ApplicationIndex['applicationId'],
    applicationStatus: JobApplicantStatus,
  ): void {
    const patchRow = (row: ConversationIndex): ConversationIndex => {
      if (row.conversationId !== conversationId) return row;
      return {
        ...row,
        primaryApplicationIndex:
          row.primaryApplicationIndex?.applicationId === applicationId
            ? { ...row.primaryApplicationIndex, applicationStatus }
            : row.primaryApplicationIndex,
        applicationIndexes: row.applicationIndexes?.map((applicationIndex) =>
          applicationIndex.applicationId === applicationId
            ? { ...applicationIndex, applicationStatus }
            : applicationIndex,
        ),
      };
    };

    this.conversationIndexes = this.conversationIndexes.map(patchRow);
    if (this.selectedConversationIndex?.conversationId === conversationId) {
      this.selectedConversationIndex = patchRow(this.selectedConversationIndex);
    }
  }

  public modifyConversationIndexPrimaryApplicationIndex(
    conversationIndex?: ConversationIndex,
    applicationIndex?: Partial<ApplicationIndex>,
  ): void {
    if (!conversationIndex || !applicationIndex) {
      return;
    }

    const updatedConversationIndexes = this.conversationIndexes.map((index) => {
      if (index.conversationId !== conversationIndex.conversationId) {
        return index;
      }

      if (index.primaryApplicationIndex) {
        return {
          ...index,
          primaryApplicationIndex: { ...index.primaryApplicationIndex, ...applicationIndex },
        };
      }

      return index;
    });

    this.conversationIndexes = updatedConversationIndexes;
  }

  private getJobDisplayTitles(
    jobIds: Map<number, Job>,
    conversationIndexes: ConversationIndex[],
  ): ConversationIndex[] {
    const updatedConversationIndexes = conversationIndexes.map((conversationIndex) => {
      const jobId = conversationIndex.primaryApplicationIndex?.jobId;
      const primaryApplicationIndex = conversationIndex.primaryApplicationIndex;

      // Update ALL applications with job display titles, location strings, and jobStatus
      const updatedApplicationIndexes = conversationIndex.applicationIndexes?.map((app) => {
        if (app.jobId) {
          const job = jobIds.get(app.jobId);
          const jobDisplayTitle = job?.displayTitle || '';
          const locationString = JobUtil.getLocation(job, { format: 'short' });
          return {
            ...app,
            jobDisplayTitle,
            locationString,
            jobStatus: job?.status ?? app.jobStatus,
          };
        }
        return app;
      });

      if (jobId) {
        const job = jobIds.get(jobId);
        const jobDisplayTitle = job?.displayTitle || '';

        // Collect all jobs for this conversation index to make them available to ApplicationDisplayUtil
        const allJobIds = conversationIndex.applicationIndexes?.map((app) => app.jobId) || [];
        const jobs = allJobIds
          .map((id) => jobIds.get(id))
          .filter((job): job is Job => job !== undefined);

        return {
          ...conversationIndex,
          primaryApplicationIndex: { ...primaryApplicationIndex, jobDisplayTitle },
          applicationIndexes: updatedApplicationIndexes,
          jobs, // Attach jobs array for ApplicationDisplayUtil to access
        };
      }
      return {
        ...conversationIndex,
        primaryApplicationIndex: {},
        applicationIndexes: updatedApplicationIndexes,
      };
    });
    return updatedConversationIndexes;
  }

  private async addPrimaryApplicationOnConversationIndexes(
    conversationIndexes: ConversationIndex[],
  ): Promise<ConversationIndex[]> {
    // 1. Extracts the jobIds from the primary application indexes
    const [, conversationIndexesWithPrimaryApplication] =
      this.getPrimaryApplicationJobIds(conversationIndexes);

    // 2. Also get all job IDs from all applications for the Applications column
    const allJobIds = new Set<number>();
    conversationIndexes.forEach((ci) => {
      ci.applicationIndexes?.forEach((app) => {
        if (app.jobId) {
          allJobIds.add(app.jobId);
        }
      });
    });

    // 3. Fetch jobs for all applications (not just primary ones)
    const jobs = await this.jobService.fetchJobs(
      [...allJobIds],
      this.meStore.employer?.id as number,
    );

    // 4. Creates a map to easily access the job by its id
    const idToJobMap = new Map<number, Job>();
    jobs.forEach((job) => idToJobMap.set(job.id, job));

    // 5. Updates the conversation indexes with the job display title and job data
    const updatedConversationIndexes = this.getJobDisplayTitles(
      idToJobMap,
      conversationIndexesWithPrimaryApplication,
    );

    return updatedConversationIndexes;
  }

  // Only the newest search update may publish its outcome, so a superseded one is never
  // mistaken for the current list.
  private beginSearchUpdate(): { requestId: number; isLatest: () => boolean } {
    const requestId = ++this.store.listFetchRequestId;
    return { requestId, isLatest: () => this.store.listFetchRequestId === requestId };
  }

  public async updateConversationIndexSearch(
    search: Partial<SearchConversationIndex>,
    opts?: {
      skipOnSameSearch?: boolean;
      resetConversationIndexes?: boolean;
      resetPagination?: boolean;
      skipTracking?: boolean;
      doNotSelectFirst?: boolean;
      signal?: AbortSignal;
    },
  ): Promise<void> {
    const existingSearch = this.conversationIndexSearch;
    const newSearchWithJobIds = { ...this.conversationIndexSearch, ...search };

    if (opts?.skipOnSameSearch && isEqual(existingSearch, newSearchWithJobIds)) {
      return;
    }

    if (opts?.resetConversationIndexes) {
      this.resetConversationIndexes();
    }

    if (opts?.resetPagination) {
      newSearchWithJobIds.pagination.page = 1;
      // Reset lastPageWithResults when pagination is reset to ensure proper pagination
      this.store.lastPageWithResults = null;
    }

    newSearchWithJobIds.applicationStatus =
      this.getDefaultApplicationStatusesIfEmpty(newSearchWithJobIds);

    this.conversationIndexSearch = newSearchWithJobIds;
    const { requestId, isLatest } = this.beginSearchUpdate();
    let succeeded = true;
    try {
      await this.fetchConversationIndexes(opts?.doNotSelectFirst, opts?.signal);
    } catch (error) {
      ErrorService.captureException(error);
      succeeded = false;
    } finally {
      if (isLatest()) {
        this.store.latestListFetchResult = {
          requestId,
          succeeded,
          rowCount: succeeded ? this.conversationIndexes.length : 0,
          autoSelectedFirst: !opts?.doNotSelectFirst,
        };
      }
      if (!opts?.skipTracking) {
        TrackingService.trackAction(
          TrackingActionName.CONVERSATIONS_FILTERED_AND_SORTED_OR_SEARCHED,
          {
            filters: {
              status: newSearchWithJobIds.applicationStatus,
              unread_only: newSearchWithJobIds.onlyUnread,
              only_current_user: newSearchWithJobIds.onlyCurrentUser,
              order_by: newSearchWithJobIds.orderBy,
            },
          },
        );
      }
    }
  }

  public async selectConversationIndex(
    conversationIndex: ConversationIndex,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) return;
    const requestId = ++this.store.selectionRequestId;
    const isCurrentSelection = () => this.store.selectionRequestId === requestId;
    try {
      this.selectingConversationIndex = true;
      if (!conversationIndex?.proId) {
        this.conversationProfileStore.abandonProfileRequest();
        return;
      }
      this.selectedConversationIndex = conversationIndex;
      this.conversationMessageService.beginConversationLoad(conversationIndex.conversationId);
      // Start eligibility with the profile request so an older selection cannot
      // launch a late status fetch after a mark/remove has refreshed eligibility.
      const profileRequest = this.proProfileService.getProProfile(
        this.meStore.employer?.id as number,
        conversationIndex.proId,
        signal,
        { isSelection: true },
      );
      const results = await Promise.allSettled([
        profileRequest,
        this.refreshSelectedProMessageStatus(false, signal),
        this.projectService.fetchProjectsTitleByJobIds(
          this.getConversationIndexJobIds([conversationIndex]),
        ),
      ]);
      const failure = results.find((result) => result.status === 'rejected');
      if (failure?.status === 'rejected') throw failure.reason;
    } catch (error) {
      throw error;
    } finally {
      if (isCurrentSelection()) {
        this.selectingConversationIndex = false;
      }
    }
  }

  public beginCardSelection(): AbortSignal {
    this.store.cardSelectionController?.abort();
    const controller = new AbortController();
    this.store.cardSelectionController = controller;
    return controller.signal;
  }

  public get hasPendingCardSelection(): boolean {
    return this.store.cardSelectionController?.signal.aborted === false;
  }

  public cancelCardSelection(): void {
    this.store.cardSelectionController?.abort();
    this.store.cardSelectionController = null;
  }

  // Claiming a generation discards the old job's in-flight fetches; the release is a no-op
  // once a newer fetch has taken the list over.
  public beginJobSwitch(): () => void {
    const { ownsGeneration } = this.claimConversationListRequest();
    const switchRequestId = this.store.conversationListRequestId;
    this.store.jobSwitchRequestId = switchRequestId;
    this.resetConversationIndexes();
    this.loadingConversationIndexes = true;
    return () => {
      if (ownsGeneration()) this.loadingConversationIndexes = false;
      if (this.store.jobSwitchRequestId === switchRequestId) this.store.jobSwitchRequestId = null;
    };
  }

  public clearSelectedProAndApplication(): void {
    this.conversationProfileStore.clearSelectedProMessageStatus();
    this.conversationProfileStore.clearSelectedProApplication();
    this.conversationProfileStore.clearSelectedPro();
    // The error card names the candidate it failed for, so it must not outlive
    // the panel it belongs to.
    this.conversationProfileStore.abandonProfileRequest();
  }

  private deduplicateConversationIndexes(
    conversationIndexes: ConversationIndex[],
  ): ConversationIndex[] {
    return unique(conversationIndexes, (index) => index.conversationId);
  }

  private async aggregateDataOnConversationIndexes(
    conversationIndexes: ConversationIndex[],
  ): Promise<ConversationIndex[]> {
    const aggregatedConversationIndexes =
      await this.addPrimaryApplicationOnConversationIndexes(conversationIndexes);
    await this.projectService.fetchProjectsTitleByJobIds(
      this.getConversationIndexJobIds(aggregatedConversationIndexes),
    );
    return aggregatedConversationIndexes;
  }

  private mergeAndDeduplicateConversationIndexes(
    conversationIndexes: ConversationIndex[],
  ): ConversationIndex[] {
    let deduplicatedConversationIndexes: ConversationIndex[] = [];
    if (this.conversationIndexSearch.pagination.page > 1) {
      // When loading more pages, append new conversations to existing ones
      deduplicatedConversationIndexes = this.deduplicateConversationIndexes([
        ...this.conversationIndexes,
        ...conversationIndexes,
      ]);
    } else {
      // For first page, replace all conversations
      deduplicatedConversationIndexes = this.deduplicateConversationIndexes(conversationIndexes);
    }
    return deduplicatedConversationIndexes;
  }

  private async fetchInternalConversationIndexes(
    shouldCommitResults?: () => boolean,
  ): Promise<ConversationIndex[]> {
    const page = this.conversationIndexSearch.pagination.page;
    const indexes = await this.persistence.fetchConversationIndexes({
      employerId: this.meStore.employer?.id as number,
      params: this.conversationIndexSearch,
    });
    if (indexes.length > 0 && (shouldCommitResults?.() ?? true)) {
      this.store.lastPageWithResults = page;
    }
    return indexes;
  }

  private async commonConversationIndexFetchActions(
    conversationIndexes: ConversationIndex[],
  ): Promise<ConversationIndex[]> {
    const aggregatedConversationIndexes =
      await this.aggregateDataOnConversationIndexes(conversationIndexes);
    return this.mergeAndDeduplicateConversationIndexes(aggregatedConversationIndexes);
  }

  public async fetchConversationIndexes(
    doNotSelectFirst = false,
    signal?: AbortSignal,
  ): Promise<void> {
    const { isCurrent, ownsGeneration } = this.claimConversationListRequest();
    this.loadingConversationIndexes = true;
    try {
      const conversationIndexes = await this.fetchInternalConversationIndexes(isCurrent);
      if (!isCurrent()) return;
      const processedConversationIndexes =
        await this.commonConversationIndexFetchActions(conversationIndexes);
      if (!isCurrent()) return;
      this.conversationIndexes = processedConversationIndexes;
      if (this.conversationIndexSearch.pagination.page === 1 && !doNotSelectFirst) {
        await this.selectConversationIndex(this.conversationIndexes[0], signal);
      }
    } catch (error) {
      throw error;
    } finally {
      if (ownsGeneration()) {
        this.loadingConversationIndexes = false;
      }
    }
  }

  /**
   * Claims the next list generation and returns the freshness test for it.
   * Request-id equality is the test on purpose: an aborted signal is not the
   * same as a discarded result, since a URL-cleaning replace re-aborts the run
   * that is currently succeeding.
   */
  private claimConversationListRequest(): {
    isCurrent: () => boolean;
    ownsGeneration: () => boolean;
  } {
    const requestId = ++this.store.conversationListRequestId;
    const employerId = this.meStore.employer?.id;
    const jobIds = [...(this.conversationIndexSearch.jobIds ?? [])];
    // Generation identity alone: the spinner must be handed to the newest
    // claimant even when the search terms were reassigned outside a claim,
    // which leaves nobody to clear it.
    const ownsGeneration = () => this.store.conversationListRequestId === requestId;
    return {
      isCurrent: () =>
        ownsGeneration() &&
        this.meStore.employer?.id === employerId &&
        isEqual(this.conversationIndexSearch.jobIds ?? [], jobIds),
      ownsGeneration,
    };
  }

  /**
   * Searches for more conversations for infinite scroll.
   * Similar to ProjectService.searchMoreProjects() - simple and focused.
   */
  public async searchMoreConversations(): Promise<void> {
    // Don't search if already loading
    if (this.loadingMoreConversations || this.loadingConversationIndexes) {
      return;
    }

    const listRequestId = this.store.conversationListRequestId;
    const isCurrent = () => this.store.conversationListRequestId === listRequestId;
    const previousPage = this.conversationIndexSearch.pagination.page;

    try {
      this.loadingMoreConversations = true;

      // Preserve all current search parameters and only update the page
      const nextPage = (this.store.lastPageWithResults || 0) + 1;
      const searchParams = {
        ...this.conversationIndexSearch,
        pagination: {
          ...this.conversationIndexSearch.pagination,
          page: nextPage,
        },
      };

      // Update the store with the new search parameters
      this.conversationIndexSearch = searchParams;

      // Fetch new conversations
      const newConversations = await this.fetchInternalConversationIndexes(isCurrent);

      // A page belongs to the list it was requested for: hand it back and leave the
      // page counter where the newer list put it.
      if (!isCurrent()) return;

      // If no results, don't update lastPageWithResults
      // This ensures we keep trying the same page number
      if (newConversations.length === 0) {
        return;
      }

      // Process and append new conversations
      const processedConversations =
        await this.aggregateDataOnConversationIndexes(newConversations);

      if (!isCurrent()) return;

      // Simple spread to append - similar to ProjectService
      this.store.conversationIndexes = [...this.conversationIndexes, ...processedConversations];
    } catch (error) {
      if (isCurrent()) {
        this.conversationIndexSearch.pagination.page = previousPage;
      }
      ErrorService.captureException(error);
      throw error;
    } finally {
      this.loadingMoreConversations = false;
    }
  }

  public async fetchConversationIndexesByApplicantId(applicationId: number): Promise<void> {
    this.loadingConversationIndexes = true;
    try {
      const conversationIndex = await this.persistence.fetchConversationIndexByApplicantId({
        employerId: this.meStore.employer?.id as number,
        applicationId,
      });

      // Remove all the other applications from the conversationIndex so we dont get other application as primary
      conversationIndex.applicationIndexes = conversationIndex.applicationIndexes?.filter(
        (index) => index.applicationId === applicationId,
      );

      // Set the jobIds to the same as the job of the application
      // So we don't fetch all the conversation indexes that are not related to the job
      const application = conversationIndex.applicationIndexes?.[0];
      if (application) {
        this.conversationIndexSearch.jobIds = [application.jobId];
      }

      const { isCurrent } = this.claimConversationListRequest();
      const conversationIndexes = await this.fetchInternalConversationIndexes(isCurrent);
      if (!isCurrent()) return;
      // Prepend the conversationIndex to the conversationIndexes array
      const processedConversationIndexes = await this.commonConversationIndexFetchActions([
        conversationIndex,
        ...conversationIndexes,
      ]);
      if (!isCurrent()) return;
      this.conversationIndexes = processedConversationIndexes;

      await this.selectConversationIndex(this.conversationIndexes[0]);
    } catch (error) {
      ErrorService.captureException(error);
    } finally {
      this.loadingConversationIndexes = false;
    }
  }

  /**
   * Fetches a single conversation by its applicant ID without altering the main
   * conversation list state.
   * @param applicationId The ID of the application.
   * @returns A single ConversationIndex or null if not found.
   */
  public async fetchConversationByApplicantId(
    applicationId: number,
  ): Promise<ConversationIndex | null> {
    try {
      const conversationIndex = await this.persistence.fetchConversationIndexByApplicantId({
        employerId: this.meStore.employer?.id as number,
        applicationId,
      });

      if (!conversationIndex) {
        return null;
      }

      // Add necessary job and project data to the single conversation.
      const [enrichedConversation] = await this.addPrimaryApplicationOnConversationIndexes([
        conversationIndex,
      ]);
      return enrichedConversation;
    } catch (error) {
      ErrorService.captureException(error);
      return null;
    }
  }

  /**
   * Fetches conversation indexes by applicant ID and updates the filters to match the
   * candidate's own status and origin, so the prepended deep-link target is never a row
   * the active status or origin filter excludes (ONE-7281). `onlyUnread` and
   * `onlyCurrentUser` are still able to pin an excluded row.
   */
  public async fetchConversationIndexesByApplicantIdWithFilters(
    applicationId: number,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) return;
    this.loadingConversationIndexes = true;
    // Claimed below, once the job this landing scopes to is known. Until then an
    // exit only owns the spinner if no newer list fetch has claimed it meanwhile.
    const entryId = this.store.conversationListRequestId;
    let isCurrent: () => boolean = () => true;
    let ownsGeneration = () => this.store.conversationListRequestId === entryId;
    try {
      const conversationIndex = await this.persistence.fetchConversationIndexByApplicantId({
        employerId: this.meStore.employer?.id as number,
        applicationId,
      });

      if (signal?.aborted) return;

      // Remove all the other applications from the conversationIndex so we dont get other application as primary
      conversationIndex.applicationIndexes = conversationIndex.applicationIndexes?.filter(
        (index) => index.applicationId === applicationId,
      );

      // Get the application details
      const application = conversationIndex.applicationIndexes?.[0];
      if (application) {
        // Update search parameters including status filters
        this.conversationIndexSearch.jobIds = [application.jobId];

        // Update status filters to match the candidate's status
        if (application.applicationStatus) {
          this.conversationIndexSearch.applicationStatus = [application.applicationStatus];
          this.selectedStatusList = [application.applicationStatus];
          this.selectedStatusFilters = [application.applicationStatus];
        }

        // Same guarantee the other two deep-link entry points give: the prepend below
        // must not pin a row the active origin filter excludes (ONE-7281). The row is
        // already scoped to this application above, so its chip reads the same origin
        // the decision is made on.
        this.conversationIndexSearch.origins = originsForDeepLinkedConversation(
          this.conversationIndexSearch.origins,
          application,
        );

        // The filter popover only persists the saved filters when the search object is
        // replaced, so the in-place edits above would never save the widened origins.
        this.conversationIndexSearch = { ...this.conversationIndexSearch };
      }

      // Claimed once the job is known: this path replaces the list just like a
      // job switch does, so a page still in flight for the previous job must not
      // append onto it.
      ({ isCurrent, ownsGeneration } = this.claimConversationListRequest());

      // Deep links must not inherit list state from a previously browsed job: a stale
      // infinite-scroll page > 1 made the fetch below miss the target and the page > 1
      // merge branch kept the previous job's list, whose first entry then got selected
      // instead of the deep-linked candidate (ONE-6335).
      this.resetConversationIndexes();
      this.conversationIndexSearch.pagination.page = 1;
      this.store.lastPageWithResults = null;

      const conversationIndexes = await this.fetchInternalConversationIndexes(isCurrent);

      if (signal?.aborted || !isCurrent()) return;

      const processedConversationIndexes = await this.commonConversationIndexFetchActions([
        conversationIndex,
        ...conversationIndexes,
      ]);

      if (!isCurrent()) return;

      // Prepend the conversationIndex to the conversationIndexes array
      this.conversationIndexes = processedConversationIndexes;

      // Select the deep-linked target directly — never conversationIndexes[0], which
      // is not guaranteed to be the target (ONE-6335).
      const targetConversation =
        this.conversationIndexes.find(
          (index) => index.conversationId === conversationIndex.conversationId,
        ) ?? conversationIndex;
      await this.selectConversationIndex(targetConversation, signal);
    } finally {
      if (ownsGeneration()) {
        this.loadingConversationIndexes = false;
      }
    }
  }

  /**
   * Fetches a single conversation by its ID without altering the main
   * conversation list state.
   * @param conversationId The ID of the conversation.
   * @returns A single ConversationIndex or null if not found.
   */
  public async fetchConversationByConversationId(
    conversationId: number,
  ): Promise<ConversationIndex | null> {
    try {
      const params: Partial<SearchConversationIndex> = {
        pagination: {
          page: 1,
          itemsPerPage: 25,
        },
        orderBy: this.conversationIndexSearch.orderBy,
        applicationStatus: this.getAllApplicationStatuses(),
        onlyCurrentUser: false,
        conversationIds: [conversationId],
      };

      const conversationIndexes = await this.persistence.fetchConversationIndexes({
        employerId: this.meStore.employer?.id as number,
        params,
      });

      if (!conversationIndexes || conversationIndexes.length === 0) {
        return null;
      }

      // Add necessary job and project data to the single conversation.
      const [enrichedConversation] =
        await this.addPrimaryApplicationOnConversationIndexes(conversationIndexes);
      return enrichedConversation;
    } catch (error) {
      ErrorService.captureException(error);
      return null;
    }
  }

  public async updateConversationWatchStatus(
    conversationId: number,
    isWatching: boolean,
  ): Promise<void> {
    const listRequestId = this.store.conversationListRequestId;
    const { isWatching: persistedIsWatching } =
      await this.persistence.updateConversationWatchStatus(conversationId, isWatching);
    // A list loaded for another job/stage while the toggle was in flight owns the store now.
    if (this.store.conversationListRequestId !== listRequestId) return;
    const updateWatchStatus = (conversationIndex: ConversationIndex): ConversationIndex => ({
      ...conversationIndex,
      isWatching: persistedIsWatching,
      readStatus: persistedIsWatching ? conversationIndex.readStatus : true,
    });

    const updatedSelectedConversationIndex =
      this.selectedConversationIndex?.conversationId === conversationId
        ? updateWatchStatus(this.selectedConversationIndex)
        : this.selectedConversationIndex;

    const updatedConversationIndexes = this.conversationIndexes.map((conversationIndex) => {
      if (conversationIndex.conversationId !== conversationId) {
        return conversationIndex;
      }

      return updateWatchStatus(conversationIndex);
    });

    TrackingService.trackAction(TrackingActionName.CONVERSATION_ALERTS_TOGGLED, {
      conversation_id: conversationId,
      alerts_enabled: persistedIsWatching,
      source: 'composer_chip',
    });

    if (!persistedIsWatching && this.shouldRemoveConversationAfterAlertsToggle()) {
      const remainingConversationIndexes = this.conversationIndexes.filter(
        (conversationIndex) => conversationIndex.conversationId !== conversationId,
      );
      this.conversationIndexes = remainingConversationIndexes;

      if (remainingConversationIndexes.length > 0) {
        await this.selectConversationIndex(remainingConversationIndexes[0]);
      } else {
        this.selectedConversationIndex = null;
        this.clearSelectedProAndApplication();
      }

      return;
    }

    this.conversationIndexes = updatedConversationIndexes;
    this.selectedConversationIndex = updatedSelectedConversationIndex;
  }

  /**
   * Fetches conversation indexes by conversation ID
   * @param conversationId - The ID of the conversation to fetch
   * @param jobId - Optional job ID to filter by
   * @param useAllStatuses - Whether to fetch all statuses (true) or respect current filters (false). Defaults to true for backward compatibility.
   */
  public async fetchConversationIndexesByConversationId(
    conversationId: number,
    jobId?: number,
    useAllStatuses = true,
  ): Promise<void> {
    this.loadingConversationIndexes = true;
    try {
      const params: Partial<SearchConversationIndex> = {
        ...this.conversationIndexSearch,
        applicationStatus: useAllStatuses
          ? this.getAllApplicationStatuses()
          : this.conversationIndexSearch.applicationStatus,
        onlyCurrentUser: false,
        conversationIds: [conversationId],
      };
      if (jobId) {
        params.jobIds = [jobId];
      } else {
        delete params.jobIds;
      }

      const conversationIndex = await this.persistence.fetchConversationIndexes({
        employerId: this.meStore.employer?.id as number,
        params,
      });

      // We want all the other conversations that are related to the same job
      // of the primary application of the conversation we are looking for
      let jobIdToSetAsDefault = jobId;
      if (!jobIdToSetAsDefault && conversationIndex[0]) {
        const primaryApplication = ProProfileService.findPrimaryApplication<ApplicationIndex>(
          conversationIndex[0].applicationIndexes,
        );
        jobIdToSetAsDefault = primaryApplication?.jobId;
      }
      this.conversationIndexSearch.jobIds = jobIdToSetAsDefault
        ? [jobIdToSetAsDefault]
        : this.conversationIndexSearch.jobIds;

      const { isCurrent } = this.claimConversationListRequest();
      const conversationIndexes = await this.fetchInternalConversationIndexes(isCurrent);
      if (!isCurrent()) return;

      // In case the query returns more than one conversationIndex, we need to filter the one we want
      const selectedConversationIndex = conversationIndex.filter(
        (index) => index.conversationId === conversationId,
      );
      // Prepend the conversationIndex to the conversationIndexes array
      const processedConversationIndexes = await this.commonConversationIndexFetchActions([
        ...selectedConversationIndex,
        ...conversationIndexes,
      ]);
      if (!isCurrent()) return;
      this.conversationIndexes = processedConversationIndexes;

      await this.selectConversationIndex(this.conversationIndexes[0]);
    } catch (error) {
      throw error;
    } finally {
      this.loadingConversationIndexes = false;
    }
  }

  private shouldRemoveConversationAfterAlertsToggle(): boolean {
    return this.conversationIndexSearch.onlyCurrentUser || this.conversationIndexSearch.onlyUnread;
  }

  public filterLocalConversationIndexesByStatus(
    applicationId: number,
    newStatus: JobApplicantStatus,
  ): AdvanceSelectedConversationResult {
    const selectedConversationId = this.selectedConversationIndex?.conversationId ?? null;

    // If there's no active filter or the new status is still in the filter, the moved
    // candidate still belongs in the list — nothing to remove, no advance needed.
    if (this.selectedStatusList.length === 0 || this.selectedStatusList.includes(newStatus)) {
      return {
        selectionAdvanced: false,
        nextSelectedConversation: this.selectedConversationIndex,
      };
    }

    // Single pass: check active-job membership, find selected index, and filter in one go.
    // The Applications tab allows changing status on applications for OTHER jobs — only
    // remove if the moved application belongs to the active job.
    const activeJobId = this.conversationIndexSearch.jobIds?.[0];
    const filtered: ConversationIndex[] = [];
    let previousSelectedIndex = -1;
    let belongsToActiveJob = false;

    for (let i = 0; i < this.conversationIndexes.length; i++) {
      const ci = this.conversationIndexes[i];
      let hasMovedApp = false;
      for (const ai of ci.applicationIndexes ?? []) {
        if (ai.applicationId === applicationId) {
          hasMovedApp = true;
          if (ai.jobId === activeJobId) belongsToActiveJob = true;
          break;
        }
      }
      if (ci.conversationId === selectedConversationId) previousSelectedIndex = i;
      if (!hasMovedApp) filtered.push(ci);
    }

    if (!belongsToActiveJob) {
      return {
        selectionAdvanced: false,
        nextSelectedConversation: this.selectedConversationIndex,
      };
    }

    this.conversationIndexes = filtered;

    return this.advanceSelectedConversationIfMissing(previousSelectedIndex);
  }

  /**
   * If the currently-selected conversation is no longer in the local list, advance the
   * selection to whatever conversation now sits at `previousSelectedIndex`, falling back
   * to the top of the list, then to null. Updates `selectedConversationIndex` in place.
   *
   * @param previousSelectedIndex - The index the previously-selected conversation occupied
   *   BEFORE the list was mutated. Pass -1 (or omit) when unknown — the fallback to top
   *   of list will be used.
   */
  public advanceSelectedConversationIfMissing(
    previousSelectedIndex = -1,
  ): AdvanceSelectedConversationResult {
    const selected = this.selectedConversationIndex;
    if (!selected) {
      return { selectionAdvanced: false, nextSelectedConversation: null };
    }

    const list = this.conversationIndexes;
    const stillInList = list.some(
      (conversationIndex) => conversationIndex.conversationId === selected.conversationId,
    );
    if (stillInList) {
      return { selectionAdvanced: false, nextSelectedConversation: selected };
    }

    const next =
      (previousSelectedIndex >= 0 ? list[previousSelectedIndex] : undefined) ?? list[0] ?? null;

    this.selectedConversationIndex = next;
    return { selectionAdvanced: true, nextSelectedConversation: next };
  }

  // Will select or unselect all the conversation indexes
  public toggleBulkActionAllConversationIndexes(isSelectAll: boolean = false): void {
    const updatedConversationIndexes = this.conversationIndexes.map((conversationIndex) => {
      return {
        ...conversationIndex,
        isBulkSelection: isSelectAll,
      };
    });
    this.conversationIndexes = updatedConversationIndexes;
  }

  public async getBulkConversationIndexes(): Promise<
    { proId: number; proProfileId: number; applicationId: number }[] | undefined
  > {
    // Lets check if there are any selected conversation
    const selectedConversationIndexes = this.conversationIndexes.filter(
      (conversationIndex) => conversationIndex.isBulkSelection,
    );
    if (selectedConversationIndexes.length === 0) {
      return;
    }

    const pros: {
      proId: number;
      proProfileId: number | undefined;
      applicationId: number | undefined;
    }[] = [];

    // Lets get the applicationIds from the selected conversation
    for (const conversationIndex of selectedConversationIndexes) {
      const pro = await this.fetchPro(conversationIndex.proId);

      pros.push({
        proId: conversationIndex.proId,
        proProfileId: pro?.pro?.id,
        applicationId: conversationIndex.primaryApplicationIndex?.applicationId,
      });
    }
    if (pros.some((pro) => !pro.applicationId || !pro.proProfileId)) {
      return;
    } else {
      return pros as { proId: number; proProfileId: number; applicationId: number }[];
    }
  }

  public async getBulkMessageConversationIndexes(): Promise<
    Pick<
      ConversationIndex,
      'conversationId' | 'proId' | 'proName' | 'proEmail' | 'primaryApplicationIndex'
    >[]
  > {
    const conversationIndexes =
      this.conversationIndexes.filter((conversationIndex) => conversationIndex.isBulkSelection) ||
      [];
    // Only pass the required properties
    const mappedConversationIndexes = conversationIndexes.map((index) => ({
      conversationId: index.conversationId,
      proId: index.proId,
      proName: index.proName,
      proEmail: index.proEmail,
      primaryApplicationIndex: index.primaryApplicationIndex,
    }));
    return mappedConversationIndexes;
  }

  private async fetchPro(proId: number): Promise<UserProfileWithRelations<'pro'> | undefined> {
    try {
      return this.proProfileService.getProProfileById(proId, this.meStore.employer?.id as number);
    } catch (error) {
      return undefined;
    }
  }

  public async refreshSelectedProMessageStatus(
    afterRemoval = false,
    signal?: AbortSignal,
  ): Promise<void> {
    const proId = this.selectedConversationIndex?.proId;
    const employerId = this.meStore.employer?.id;
    if (!proId || !employerId) return;
    const store = this.conversationProfileStore;
    const requestId = ++store.messageStatusRequestId;
    const blocksUntilRefreshed = afterRemoval || store.messageStatusRefreshState !== 'idle';
    // Only a committed removal introduces a blocking refresh/retry state.
    if (blocksUntilRefreshed) {
      store.selectedProMessageStatus = undefined;
      store.messageStatusRefreshState = 'loading';
    }
    const isCurrent = () =>
      !signal?.aborted &&
      store.messageStatusRequestId === requestId &&
      this.selectedConversationIndex?.proId === proId &&
      this.meStore.employer?.id === employerId;
    try {
      const status = await this.conversationMessageService.getUserMessageStatus(proId, employerId);
      if (!isCurrent()) return;
      store.setSelectedProMessageStatus(status);
      store.messageStatusRefreshState = 'idle';
    } catch (error) {
      if (!isCurrent()) return;
      if (!blocksUntilRefreshed) throw error;
      store.messageStatusRefreshState = 'error';
      ErrorService.captureException(error);
    }
  }

  public async fetchConversationIndexesByProData(
    proData: string,
    opts?: { strictIdentityMatch?: boolean },
  ): Promise<{
    count: number;
    headers: ConversationIndex[];
    /** Strict mode only; missing (legacy path, older backends) means false. */
    hasMore?: boolean;
  }> {
    const employerId = this.meStore.employer?.id as number;
    const result = await this.persistence.fetchConversationIndexesByProData({
      employerId,
      proData,
      // Only forwarded when explicitly requested so Candidate Archive keeps
      // its current matching behavior (ONE-6912).
      ...(opts?.strictIdentityMatch ? { strictIdentityMatch: true } : {}),
    });

    // Add job data to conversation indexes (reuse existing pattern)
    if (result.headers.length > 0) {
      result.headers = await this.addPrimaryApplicationOnConversationIndexes(result.headers);
    }

    return result;
  }

  // ==========================================================================
  // Account-wide candidate search (ONE-6912 "Jump to any candidate")
  // ==========================================================================

  public get globalSearchQuery(): string {
    return this.store.globalSearchQuery;
  }

  public set globalSearchQuery(query: string) {
    this.store.globalSearchQuery = query;
  }

  public get globalSearchResults(): ConversationIndex[] {
    return this.store.globalSearchResults;
  }

  public get globalSearchHasMore(): boolean {
    return this.store.globalSearchHasMore;
  }

  public get globalSearchLoading(): boolean {
    return this.store.globalSearchLoading;
  }

  public get globalSearchError(): boolean {
    return this.store.globalSearchError;
  }

  public get globalSearchSettledQuery(): string {
    return this.store.globalSearchSettledQuery;
  }

  /**
   * Runs the account-wide identity lookup for the Candidates pane. Fully
   * isolated from the resting job/stage list — it only writes the
   * globalSearch* store state. Stale responses are dropped so an older
   * request can never replace a newer query's results.
   */
  public async searchGlobalCandidates(query: string): Promise<void> {
    // Normalize once at the boundary: eligibility, the request, the
    // settled-query comparison, and analytics all use the trimmed query, so a
    // pasted " marcus " can never LIKE-match literal whitespace server-side.
    const trimmedQuery = query.trim();

    if (!isEligibleGlobalSearchQuery(trimmedQuery)) {
      this.resetGlobalSearchResults();
      return;
    }

    // Phone-like queries go over the wire digit-normalized. The strict
    // backend strips the same characters, and older backends (that ignore
    // `strictIdentityMatch`) only match phone numbers for digit-only queries,
    // so punctuated phone searches keep working across deploy windows.
    const isPhoneQuery = isPhoneLikeQuery(trimmedQuery);
    const requestQuery = isPhoneQuery ? normalizePhoneQueryDigits(trimmedQuery) : trimmedQuery;

    const requestId = ++this.store.globalSearchRequestId;
    this.store.globalSearchLoading = true;
    this.store.globalSearchError = false;

    try {
      const result = await this.fetchConversationIndexesByProData(requestQuery, {
        strictIdentityMatch: true,
      });

      // A newer query has been issued while this one was in flight — drop it.
      if (requestId !== this.store.globalSearchRequestId) return;

      const headers = result.headers ?? [];
      this.store.globalSearchResults = headers;
      // New backend: an explicit hasMore flag (count is the returned-row
      // count). Old backend: no flag but an exact total, so more matched than
      // returned means the endpoint cap kicked in. Deriving from both keeps
      // the cap notice honest whichever side deploys first.
      this.store.globalSearchHasMore = result.hasMore === true || result.count > headers.length;
      this.store.globalSearchSettledQuery = trimmedQuery;

      TrackingService.trackAction(TrackingActionName.GLOBAL_CANDIDATE_SEARCH_PERFORMED, {
        query_length: trimmedQuery.length,
        is_phone_query: isPhoneQuery,
        // Loaded candidates (≤ 100); the strict endpoint reports no exact total.
        candidate_count: this.store.globalSearchResults.length,
        has_more: this.store.globalSearchHasMore,
        has_results: this.store.globalSearchResults.length > 0,
        job_id: this.conversationIndexSearch.jobIds?.[0],
      });
    } catch (error) {
      if (requestId !== this.store.globalSearchRequestId) return;

      this.store.globalSearchError = true;
      this.store.globalSearchResults = [];
      this.store.globalSearchHasMore = false;
      this.store.globalSearchSettledQuery = trimmedQuery;

      ErrorService.captureException(error);
      TrackingService.trackAction(TrackingActionName.GLOBAL_CANDIDATE_SEARCH_FAILED, {
        query_length: trimmedQuery.length,
        is_phone_query: isPhoneQuery,
      });
    } finally {
      if (requestId === this.store.globalSearchRequestId) {
        this.store.globalSearchLoading = false;
      }
    }
  }

  /**
   * Clears results without touching the live query text. Used when the query
   * drops below the minimum length: the field keeps what the user typed, but
   * no results (and no empty state) may render for an ineligible query.
   */
  public resetGlobalSearchResults(): void {
    // Invalidate any in-flight request so a late response cannot land.
    this.store.globalSearchRequestId++;
    this.store.globalSearchResults = [];
    this.store.globalSearchHasMore = false;
    this.store.globalSearchLoading = false;
    this.store.globalSearchError = false;
    this.store.globalSearchSettledQuery = '';
  }

  /**
   * Fully clears the account-wide search (query included). The resting
   * conversation list, selected status filters, pagination, and selection are
   * intentionally untouched — search never mutated them.
   */
  public clearGlobalCandidateSearch(): void {
    this.store.globalSearchQuery = '';
    this.resetGlobalSearchResults();
  }

  private getDefaultApplicationStatusesIfEmpty(search: Partial<SearchConversationIndex>): string[] {
    if (search.applicationStatus && search.applicationStatus.length !== 0) {
      return search.applicationStatus;
    }
    return [
      JobApplicantStatus.CONTACT,
      JobApplicantStatus.NEW,
      JobApplicantStatus.CLIENT,
      JobApplicantStatus.REVIEW,
      JobApplicantStatus.INTERVIEW,
      JobApplicantStatus.OFFER,
      JobApplicantStatus.HIRED,
    ];
  }

  private getAllApplicationStatuses(): string[] {
    return [
      JobApplicantStatus.CONTACT,
      JobApplicantStatus.NEW,
      JobApplicantStatus.CLIENT,
      JobApplicantStatus.REVIEW,
      JobApplicantStatus.INTERVIEW,
      JobApplicantStatus.OFFER,
      JobApplicantStatus.HIRED,
      JobApplicantStatus.REJECTED,
    ];
  }
}
